import { useState, useEffect, useRef, useMemo } from "react";
import {
  CalendarRange, Plus, Trash2, Soup, Utensils,
  ChevronLeft, ChevronRight, ChevronDown, X, Save, BookOpen, Loader2, Eye, EyeOff, Search,
} from "lucide-react";
import { Button, Modal, DatePicker, ValidUntilPicker } from "../../../components";
import axios from "axios";
import {
  GET_ADMIN_DAILY_MENU_URL,
  SAVE_DAILY_MENU_URL,
  PATCH_DAILY_MENU_URL,
  DELETE_DAILY_MENU_URL,
  GET_MANAGER_DAILY_MENU_URL,
  SAVE_MANAGER_DAILY_MENU_URL,
  PATCH_MANAGER_DAILY_MENU_URL,
  DELETE_MANAGER_DAILY_MENU_URL,
  GET_MENU_ITEMS_URL,
  GET_TAGS_URL,
  GET_STANDARD_MENU_PRICE_URL,
} from "../../../features/UrlHelper";
import { useAuth } from "../../../context/AuthContext";
import { parseDbUtcDate } from "../../../features/dateFormaters";
import { ITEM_TYPE_META } from "../../../features/orderConstants";
import CreateMealModal from "./CreateMealModal";
import "./DailyMenuSection.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const DAY_NAMES_SK = ["Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota", "Nedeľa"];
const DAY_SHORT_SK = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];

// ─── Date helpers ──────────────────────────────────────────────────────────────

function getSundayOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? 0 : 7 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function getMondayOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun, 1=Mon, …
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizeToISODate(value, fallback = "") {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toISODate(value);
  }

  if (typeof value === "string") {
    const onlyDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (onlyDate) return value;

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return toISODate(parsed);
    }
  }

  return fallback;
}

function formatDateSK(date) {
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
}

function formatLastUsedDateSK(dateValue) {
  if (!dateValue) return null;

  const raw = String(dateValue);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [, year, month, day] = match;
  return `${day}. ${month}. ${year}`;
}

function getDayNameSK(date) {
  const idx = (date.getDay() + 6) % 7; // Mon=0 … Sun=6
  return DAY_NAMES_SK[idx];
}

function getDayShortSK(date) {
  return DAY_SHORT_SK[(date.getDay() + 6) % 7];
}

function getWeekDays(monday) {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

function isToday(date) {
  return toISODate(date) === toISODate(new Date());
}

// ─── Slovak public holidays (ported from C# algorithm) ───────────────────────

function getEasterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day   = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

const _holidayCache = new Map();

function getSlovakHolidays(year) {
  if (_holidayCache.has(year)) return _holidayCache.get(year);
  const map = new Map();
  const s = (mo, d, n) =>
    map.set(`${year}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`, n);
  s(1,  1,  "De\u0148 vzniku Slovenskej republiky");
  s(1,  6,  "Zjavenie P\u00e1na (Traja kr\u00e1li)");
  s(5,  1,  "Sviatok pr\u00e1ce");
  s(5,  8,  "De\u0148 v\u00ed\u0165azstva nad fa\u0161izmom");
  s(7,  5,  "Sviatok sv\u00e4t\u00e9ho Cyrila a Metoda");
  s(8,  29, "V\u00fdro\u010die SNP");
  s(9,  1,  "De\u0148 \u00dastavy Slovenskej republiky");
  s(9,  15, "Sedembolestn\u00e1 Panna M\u00e1ria");
  s(11, 1,  "Sviatok v\u0161etk\u00fdch sv\u00e4t\u00fdch");
  s(11, 17, "De\u0148 boja za slobodu a demokraciu");
  s(12, 24, "\u0160tedr\u00fd de\u0148");
  s(12, 25, "Prv\u00fd sviatok viano\u010dn\u00fd");
  s(12, 26, "Druh\u00fd sviatok viano\u010dn\u00fd");
  const easter = getEasterSunday(year);
  const gf = new Date(easter); gf.setDate(easter.getDate() - 2);
  const em = new Date(easter); em.setDate(easter.getDate() + 1);
  map.set(toISODate(gf), "Veľký piatok");
  map.set(toISODate(em), "Veľkonočný pondelok");
  _holidayCache.set(year, map);
  return map;
}

function getHolidayName(date) {
  return getSlovakHolidays(date.getFullYear()).get(toISODate(date)) || null;
}

function isWeekend(date) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

function groupByType(list) {
  return Object.entries(ITEM_TYPE_META)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([type, meta]) => ({ type, meta, items: list.filter((i) => i.item_type === type) }))
    .filter((g) => g.items.length > 0);
}

// Vráti predvyplnenú cenu pre nové jedlo (fallback mechanizmus)
function getDefaultPrice(dayKey, schedule) {
  // 1. Skús aktuálny deň
  const dayEntries = (schedule[dayKey] || []).filter((e) => !e.isDeleted);
  if (dayEntries.length > 0) {
    return dayEntries[dayEntries.length - 1].price ?? "";
  }
  // 2. Hľadaj dozadu v predchádzajúcich dňoch
  const dayKeys = Object.keys(schedule)
    .filter((k) => k !== "stale")
    .sort();
  const currentIdx = dayKeys.indexOf(dayKey);
  for (let i = currentIdx - 1; i >= 0; i--) {
    const prevEntries = (schedule[dayKeys[i]] || []).filter((e) => !e.isDeleted);
    if (prevEntries.length > 0) {
      return prevEntries[prevEntries.length - 1].price ?? "";
    }
  }
  // 3. Fallback
  return "";
}

// FIX 3: parse price string, handles comma as decimal separator
function parsePrice(raw) {
  const cleaned = String(raw ?? "").replace(",", ".");
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}

// Cena položky menu musí byť vždy kladná – menu s cenou 0 sa nikdy nesmie uložiť.
function isValidPrice(raw) {
  const cleaned = String(raw ?? "").trim().replace(",", ".");
  if (cleaned === "") return false;
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0;
}

// Kľúč pre označenie konkrétneho riadka s chybnou cenou (deň + entry sú unikátne spolu)
function priceErrorKey(dayKey, entryKey) {
  return `${dayKey}::${entryKey}`;
}

// Suggest next menu_code for the given item_type based on already used codes
function nextCode(existingEntries, itemType) {
  const active = existingEntries.filter((e) => !e.isDeleted);
  if (itemType === "SOUP") {
    // Generuj celú abecedu (A-Z)
    const alphabet = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
    const used = active.filter((e) => e.item_type === "SOUP").map((e) => (e.menu_code || "").trim());
    return alphabet.find((l) => !used.includes(l)) || "";
  } else {
    const used = active.filter((e) => e.item_type !== "SOUP").map((e) => (e.menu_code || "").trim());
    // Parsuj čísla z used poľa a ignoruj NaN
    const usedNumbers = used.map(s => parseInt(s, 10)).filter(n => !isNaN(n));
    
    // Hľadaj prvé voľné číslo (dopĺňaj "diery")
    let nextNum = 1;
    while (usedNumbers.includes(nextNum)) {
      nextNum++;
    }
    return nextNum.toString();
  }
}

// ─── ItemPicker ───────────────────────────────────────────────────────────────

function ItemPicker({ catalogItems, topTags, onSelect, onClose, defaultTab, onTabChange, onCreateNew }) {
  const [search, setSearch] = useState("");
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const inputRef = useRef(null);

  // Determine available tabs from catalog (sorted by ITEM_TYPE_META order)
  const availableTabs = useMemo(() => {
    const types = new Set(catalogItems.map((i) => i.item_type));
    return Object.entries(ITEM_TYPE_META)
      .filter(([type]) => types.has(type))
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([type, meta]) => ({ type, meta }));
  }, [catalogItems]);

  const [activeTab, setActiveTab] = useState(() => {
    const typesByOrder = Object.entries(ITEM_TYPE_META)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([type]) => type);
    const firstAvailable = typesByOrder.find((type) => catalogItems.some((i) => i.item_type === type)) || "SOUP";
    // Use last-used category if it exists in the current catalog, otherwise fall back
    if (defaultTab && catalogItems.some((i) => i.item_type === defaultTab)) return defaultTab;
    return firstAvailable;
  });

  const handleTabChange = (type) => {
    setActiveTab(type);
    onTabChange?.(type);
  };

  // All tags present across catalog items
  const allTagsFromCatalog = useMemo(() => {
    const map = new Map();
    catalogItems.forEach((item) => {
      if (Array.isArray(item.tags)) {
        item.tags.forEach((tag) => {
          if (!map.has(tag.id)) map.set(tag.id, tag);
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogItems]);

  // Tag filter derived values
  const tagSuggestions = useMemo(() => {
    if (!tagInput.trim()) return [];
    const q = tagInput.toLowerCase();
    return allTagsFromCatalog.filter(
      (tag) => tag.name.toLowerCase().includes(q) && !selectedFilterTags.includes(tag.id)
    );
  }, [allTagsFromCatalog, tagInput, selectedFilterTags]);

  const topTagsFiltered = useMemo(
    () =>
      topTags
        .filter((t) => allTagsFromCatalog.some((at) => at.id === t.id) && !selectedFilterTags.includes(t.id))
        .slice(0, 10),
    [topTags, allTagsFromCatalog, selectedFilterTags]
  );

  const selectedTagObjects = useMemo(
    () => selectedFilterTags.map((id) => allTagsFromCatalog.find((t) => t.id === id)).filter(Boolean),
    [selectedFilterTags, allTagsFromCatalog]
  );

  const addFilterTag = (tag) => {
    if (selectedFilterTags.includes(tag.id)) return;
    setSelectedFilterTags((prev) => [...prev, tag.id]);
    setTagInput("");
    setShowTagSuggestions(false);
  };

  const removeFilterTag = (tagId) => {
    setSelectedFilterTags((prev) => prev.filter((id) => id !== tagId));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (tagSuggestions.length > 0) addFilterTag(tagSuggestions[0]);
    } else if (e.key === "Escape") {
      setShowTagSuggestions(false);
    }
  };

  // Combined filter: activeTab + text search + selected tags
  const filteredItems = useMemo(() => {
    return catalogItems.filter((item) => {
      if (item.item_type !== activeTab) return false;
      if (search.trim() && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (selectedFilterTags.length > 0) {
        if (
          !selectedFilterTags.every(
            (tagId) => Array.isArray(item.tags) && item.tags.some((t) => t.id === tagId)
          )
        )
          return false;
      }
      return true;
    });
  }, [catalogItems, activeTab, search, selectedFilterTags]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const hasTagsArea =
    selectedTagObjects.length > 0 || (!tagInput && topTagsFiltered.length > 0);

  return (
    <div className="dm-picker">

      {/* 1. ZÁLOŽKY + tlačidlo zavreť */}
      <div className="dm-picker__tabs">
        {availableTabs.map(({ type, meta }) => {
          const Icon = meta.Icon;
          return (
            <button
              key={type}
              type="button"
              className={`dm-picker__tab${activeTab === type ? " dm-picker__tab--active" : ""}`}
              onClick={() => handleTabChange(type)}
            >
              <Icon size={12} />
              <span>{meta.labelSingular}</span>
            </button>
          );
        })}
        <button className="dm-picker__close" onClick={onClose} aria-label="Zavrieť">
          <X size={15} />
        </button>
      </div>

      {/* 2. VYHADÁVANIE + FILTER TAGOV vedla seba */}
      <div className="dm-picker__search-row">
        <input
          ref={inputRef}
          className="dm-picker__search"
          placeholder="Hľadaj jedlo…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="dm-picker__tag-input-wrap">
          <Search size={12} className="dm-picker__tag-search-icon" />
          <input
            className="dm-picker__tag-input"
            value={tagInput}
            onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
            onKeyDown={handleTagKeyDown}
            onFocus={() => tagInput && setShowTagSuggestions(true)}
            onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
            placeholder="Filtrovať podľa tagu..."
            autoComplete="off"
          />
          {showTagSuggestions && tagSuggestions.length > 0 && (
            <div className="dm-picker__tag-dropdown">
              {tagSuggestions.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className="dm-picker__tag-suggestion"
                  onMouseDown={() => addFilterTag(tag)}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 3. VYBRANÉ TAGY + POPULÁRNE TAGY */}
      {hasTagsArea && (
        <div className="dm-picker__tags-area">
          {selectedTagObjects.length > 0 && (
            <div className="dm-picker__active-tags">
              {selectedTagObjects.map((tag) => (
                <span key={tag.id} className="dm-picker__tag-chip">
                  {tag.name}
                  <button
                    type="button"
                    className="dm-picker__tag-chip-remove"
                    onClick={() => removeFilterTag(tag.id)}
                    aria-label={`Odstrániť filter ${tag.name}`}
                  >
                    <X size={9} />
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="dm-picker__tags-clear"
                onClick={() => setSelectedFilterTags([])}
              >
                <X size={11} /> Zrušiť
              </button>
            </div>
          )}
          {!tagInput && topTagsFiltered.length > 0 && (
            <div className="dm-picker__popular-tags">
              <span className="dm-picker__popular-label">Populárne:</span>
              {topTagsFiltered.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className="dm-picker__popular-tag"
                  onClick={() => addFilterTag(tag)}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 4. ZOZNAM JEDÁL */}
      <div className="dm-picker__list">
        {onCreateNew && (
          <button
            type="button"
            className="dm-picker__add-new-btn"
            onClick={() => onCreateNew(activeTab)}
          >
            <Plus size={13} />
            Pridať nové jedlo
          </button>
        )}
        {filteredItems.length === 0 ? (
          <p className="dm-picker__empty">Žiadne výsledky</p>
        ) : (
          filteredItems.map((item) => (
            <button
              key={item.id}
              className="dm-picker__item"
              onClick={() => onSelect(item)}
            >
              <span className="dm-picker__item-name">{item.name}</span>
              {item.last_used_date ? (
                <span className="dm-picker__last-used-badge">
                  Naposledy: {formatLastUsedDateSK(item.last_used_date)}
                </span>
              ) : (
                <span className="dm-picker__last-used-badge dm-picker__last-used-badge--new">
                  Nové
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ─── EntryRow ─────────────────────────────────────────────────────────────────

function EntryRow({ entry, onDelete, onUpdateCode, onUpdatePrice, isStale, onUpdateValidUntil, modalSunday, hasPriceError }) {
  return (
    <div className={`dm-entry${entry.isNew ? " dm-entry--new" : ""}${isStale ? " dm-entry--stale" : ""}`}>
      <input
        className="dm-entry__code"
        value={entry.menu_code || ""}
        onChange={(e) => onUpdateCode(e.target.value.toUpperCase())}
        placeholder="Kód"
        maxLength={2}
        title="Kód menu (A/B/1/2…)"
      />
      <span className="dm-entry__name" title={entry.name}>
        {entry.name}
      </span>
      {isStale && onUpdateValidUntil && (
        <ValidUntilPicker
          value={entry.valid_until || null}
          onChange={onUpdateValidUntil}
          modalSunday={modalSunday}
        />
      )}
      <input
        className={`dm-entry__price${hasPriceError ? " dm-entry__price--error" : ""}`}
        type="text"
        inputMode="decimal"
        value={entry.price ?? ""}
        onChange={(e) => onUpdatePrice(e.target.value)}
        placeholder="Cena v €"
        title={hasPriceError ? "Cena musí byť kladné číslo" : "Cena"}
        aria-invalid={hasPriceError ? "true" : undefined}
      />
      <button className="dm-entry__delete" onClick={onDelete} title="Odstrániť">
        <Trash2 size={13} />
      </button>
    </div>
  );
}

// ─── DaySection (used inside the modal) ──────────────────────────────────────

function DaySection({ dayKey, dayLabel, entries, catalogItems, topTags, onAdd, onDelete, onUpdateCode, onUpdatePrice, onUpdateValidUntil, isStale, allEntries, onCreateNew, modalSunday, priceErrorKeys }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [lastUsedCategory, setLastUsedCategory] = useState(null);

  // Pre stály deň filtrujeme podľa vlastných entries; pre bežný deň aj podľa stáleho menu (allEntries = dayEntries + staleEntries)
  const usedItemIds = new Set(
    (isStale ? entries : (allEntries ?? entries))
      .filter((e) => !e.isDeleted)
      .map((e) => e.menu_item_id)
  );
  const filteredCatalogItems = catalogItems.filter((item) => !usedItemIds.has(item.id));

  const dayDate     = !isStale && dayKey !== "stale" ? new Date(dayKey) : null;
  const holidayName = dayDate ? getHolidayName(dayDate) : null;
  const weekend     = dayDate ? isWeekend(dayDate) : false;
  const today       = !isStale && dayKey === toISODate(new Date());

  const sectionClass = [
    "dm-day-section",
    isStale                                ? "dm-day-section--stale"   : "",
    !isStale && holidayName                ? "dm-day-section--holiday" : "",
    !isStale && !holidayName && weekend    ? "dm-day-section--weekend" : "",
    today                                  ? "dm-day-section--today"   : "",
  ].filter(Boolean).join(" ");

  const visible = entries.filter((e) => !e.isDeleted);
  const byType = groupByType(visible);

  function handleSelect(catalogItem) {
    setLastUsedCategory(catalogItem.item_type);
    onAdd(dayKey, catalogItem);
    setPickerOpen(false);
  }

  return (
    <div className={sectionClass}>
      <div className="dm-day-section__header">
        <div className="dm-day-section__label-group">
          <span className="dm-day-section__label">{dayLabel}</span>
          {today && <span className="dm-day-section__today-badge">DNES</span>}
          {holidayName && <span className="dm-day-section__holiday-badge">{holidayName}</span>}
        </div>
        <button
          className="dm-day-section__add-btn"
          onClick={() => setPickerOpen((v) => !v)}
          title="Pridať jedlo"
        >
          <Plus size={13} />
          <span>Pridať</span>
        </button>
      </div>

      {pickerOpen && (
        <ItemPicker
          catalogItems={filteredCatalogItems}
          topTags={topTags}
          onSelect={handleSelect}
          onClose={() => setPickerOpen(false)}
          defaultTab={lastUsedCategory}
          onTabChange={setLastUsedCategory}
          onCreateNew={onCreateNew ? (activeTab) => { setPickerOpen(false); onCreateNew(dayKey, activeTab); } : undefined}
        />
      )}

      {visible.length === 0 && !pickerOpen && (
        <p className="dm-day-section__empty">Žiadne jedlá pre tento deň</p>
      )}

      {byType.map(({ type, meta, items }) => {
        const { Icon, label } = meta;
        return (
          <div key={type} className="dm-day-section__group">
            <div className="dm-day-section__group-label">
              <Icon size={12} /> {label}
            </div>
            {items.map((entry) => (
              <EntryRow
                key={entry.key}
                entry={entry}
                onDelete={() => onDelete(dayKey, entry.key)}
                onUpdateCode={(code) => onUpdateCode(dayKey, entry.key, code)}
                onUpdatePrice={(price) => onUpdatePrice(dayKey, entry.key, price)}
                isStale={isStale}
                onUpdateValidUntil={onUpdateValidUntil ? (val) => onUpdateValidUntil(dayKey, entry.key, val) : undefined}
                modalSunday={modalSunday}
                hasPriceError={Boolean(priceErrorKeys?.has(priceErrorKey(dayKey, entry.key)))}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ─── BoardItemCard (main board view) ─────────────────────────────────────────

const UNAVAIL_PRESETS = [
  { label: "10 min", mins: 10 },
  { label: "20 min", mins: 20 },
  { label: "30 min", mins: 30 },
  { label: "45 min", mins: 45 },
  { label: "1 hod",  mins: 60 },
];

function timeAfterMinutes(mins) {
  const d = new Date(Date.now() + mins * 60_000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function BoardItemCard({ item, onToggleUnavailable }) {
  const [formOpen, setFormOpen] = useState(false);
  const [untilVal, setUntilVal] = useState("");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const isUnavail = Boolean(item.unavailable) && (!item.unavailable_until || parseDbUtcDate(item.unavailable_until) > new Date());
  const meta = ITEM_TYPE_META[item.item_type] || ITEM_TYPE_META.MENU;
  const Icon = meta.Icon;

  const untilFormatted = item.unavailable_until
    ? parseDbUtcDate(item.unavailable_until)?.toLocaleString("sk-SK", { dateStyle: "short", timeStyle: "short" }) ?? null
    : null;

  // Time is valid only when >= now + 10 min
  function isTimeValid(timeStr) {
    if (!timeStr) return false;
    const [h, m] = timeStr.split(":").map(Number);
    const sel = new Date();
    sel.setHours(h, m, 0, 0);
    return sel.getTime() >= Date.now() + 10 * 60_000;
  }

  function handlePreset(mins) {
    setSelectedPreset(mins);
    setUntilVal(timeAfterMinutes(mins));
  }

  function handleTimeChange(val) {
    setSelectedPreset(null);
    setUntilVal(val);
  }

  function markUnavailable(timeStr) {
    let until = null;
    if (timeStr) {
      const [h, m] = timeStr.split(":").map(Number);
      const d = new Date();
      d.setHours(h, m, 0, 0);
      until = d.toISOString();
    }
    onToggleUnavailable(item.id, true, until);
    setFormOpen(false);
    setUntilVal("");
    setSelectedPreset(null);
  }

  return (
    <div className={`dm-card${isUnavail ? " dm-card--unavailable" : ""}`}>
      <div className="dm-card__main">
        {item.menu_code && <span className="dm-card__code">{item.menu_code}</span>}
        <Icon size={15} className="dm-card__type-icon" />
        <div className="dm-card__info">
          <span className="dm-card__name">{item.name}</span>
          {item.description && <span className="dm-card__desc">{item.description}</span>}
          {item.allergens_list && (
            <div className="dm-card__allergens">
              {item.allergens_list.split(",").map((n) => (
                <span key={n} className="dm-card__allergen-tag">{n.trim()}</span>
              ))}
            </div>
          )}
        </div>
        <div className="dm-card__meta">
          {item.price > 0 && (
            <span className="dm-card__price">{Number(item.price).toFixed(2)} €</span>
          )}
          {isUnavail ? (
            <button
              className="dm-card__avail-btn dm-card__avail-btn--restore"
              onClick={() => onToggleUnavailable(item.id, false, null)}
              title="Obnoviť dostupnosť"
            >
              <Eye size={14} />
            </button>
          ) : (
            <button
              className="dm-card__avail-btn"
              onClick={() => setFormOpen((v) => !v)}
              title="Označiť ako nedostupné"
            >
              <EyeOff size={14} />
            </button>
          )}
        </div>
      </div>

      {isUnavail && (
        <div className="dm-card__unavail-badge">
          Nedostupné{untilFormatted ? ` do ${untilFormatted}` : " — natrvalo"}
        </div>
      )}

      {formOpen && !isUnavail && (
        <div className="dm-card__unavail-form">
          {/* Row 1: label + quick presets */}
          <div className="dm-card__unavail-top">
            <span className="dm-card__unavail-label">Nedostupné do:</span>
            <div className="dm-card__unavail-presets">
              {UNAVAIL_PRESETS.map(({ label, mins }) => (
                <button
                  key={mins}
                  className={`dm-card__unavail-preset${selectedPreset === mins ? " dm-card__unavail-preset--active" : ""}`}
                  onClick={() => handlePreset(mins)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {/* Row 2: time input + action buttons */}
          <div className="dm-card__unavail-row">
            <input
              type="time"
              className="dm-card__unavail-until"
              value={untilVal}
              min={timeAfterMinutes(10)}
              onChange={(e) => handleTimeChange(e.target.value)}
            />
            <div className="dm-card__unavail-actions">
              <button
                className="dm-card__unavail-confirm"
                onClick={() => markUnavailable(untilVal)}
                disabled={!isTimeValid(untilVal)}
              >
                Potvrdiť
              </button>
              <button className="dm-card__unavail-all" onClick={() => markUnavailable(null)}>
                Natrvalo
              </button>
              <button className="dm-card__unavail-cancel" onClick={() => setFormOpen(false)}>
                <X size={13} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CollapsibleDayView – one row per day, expand/collapse ──────────────────

function CollapsibleDayView({ date, items, onToggleUnavailable }) {
  const today       = isToday(date);
  const weekend     = isWeekend(date);
  const holidayName = getHolidayName(date);
  const [open, setOpen] = useState(today);

  const byType = groupByType(items);

  const cardClass = [
    "dm-day-card",
    today                              ? "dm-day-card--today"   : "",
    !today && holidayName              ? "dm-day-card--holiday" : "",
    !today && !holidayName && weekend  ? "dm-day-card--weekend" : "",
    open                               ? "dm-day-card--open"    : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClass}>
      <button className="dm-day-card__header" onClick={() => setOpen((v) => !v)}>
        <div className="dm-day-card__title">
          <span className="dm-day-card__name">{getDayNameSK(date)}</span>
          <span className="dm-day-card__date">{formatDateSK(date)}</span>
          {today && <span className="dm-day-card__today-badge">Dnes</span>}
          {holidayName && <span className="dm-day-card__holiday-badge">{holidayName}</span>}
        </div>
        <div className="dm-day-card__right">
          {items.length > 0 && (
            <span className="dm-day-card__count">{items.length}</span>
          )}
          <ChevronDown size={16} className={`dm-day-card__chevron${open ? " dm-day-card__chevron--open" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="dm-day-card__body">
          {items.length === 0 ? (
            <p className="dm-day-card__empty">Žiadne menu pre tento deň</p>
          ) : (
            <>
              {byType.map(({ type, meta, items }) => {
                const { Icon, label } = meta;
                return (
                  <div key={type} className="dm-day-card__section">
                    <div className="dm-day-card__section-header">
                      <Icon size={14} /><span>{label}</span>
                    </div>
                    <div className="dm-day-card__cards">
                      {items.map((item) => (
                        <BoardItemCard key={item.id} item={item} onToggleUnavailable={onToggleUnavailable} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function DailyMenuSection() {
  const { userRole } = useAuth();
  const isManager = userRole === "MANAGER";

  // View filter (defaults to current week Mon–Sun)
  const thisMonday = getMondayOfWeek(new Date());
  const [viewFrom,     setViewFrom]     = useState(toISODate(thisMonday));
  const [viewTo,       setViewTo]       = useState(toISODate(addDays(thisMonday, 6)));
  const [menuData,     setMenuData]     = useState([]);
  const [loading,      setLoading]      = useState(true);

  // Weekly menu builder modal
  const [modalOpen,      setModalOpen]      = useState(false);
  const [modalWeekStart, setModalWeekStart] = useState(toISODate(thisMonday));
  const [modalSchedule,  setModalSchedule]  = useState({});
  const [catalogItems,   setCatalogItems]   = useState([]);
  const [topTags,        setTopTags]        = useState([]);
  const [modalLoading,   setModalLoading]   = useState(false);
  const [savingModal,    setSavingModal]    = useState(false);
  const [saveMsg,        setSaveMsg]        = useState("");
  const [saveMsgIsError, setSaveMsgIsError] = useState(false);
  const [priceErrorKeys, setPriceErrorKeys] = useState(() => new Set());
  const priceLookupRequests = useRef(new Map());

  // Create new meal from within the weekly builder
  const [isCreateMealOpen,    setIsCreateMealOpen]    = useState(false);
  const [pendingDayForNewMeal, setPendingDayForNewMeal] = useState(null);
  const [pendingMealCategory,  setPendingMealCategory]  = useState(null);


  const getDailyMenuUrl = (params) => isManager
    ? GET_MANAGER_DAILY_MENU_URL(params.date_from, params.date_to)
    : GET_ADMIN_DAILY_MENU_URL(params);
  const saveDailyMenuUrl = isManager ? SAVE_MANAGER_DAILY_MENU_URL : SAVE_DAILY_MENU_URL;
  const patchDailyMenuUrl = isManager ? PATCH_MANAGER_DAILY_MENU_URL : PATCH_DAILY_MENU_URL;
  const deleteDailyMenuUrl = isManager ? DELETE_MANAGER_DAILY_MENU_URL : DELETE_DAILY_MENU_URL;

  // ── Load board view ──────────────────────────────────────────────────────────

  useEffect(() => {
    loadMenuData();
  }, [viewFrom, viewTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios
      .get(GET_TAGS_URL())
      .then((res) => setTopTags(res.data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMenuData() {
    setLoading(true);
    try {
      const url = getDailyMenuUrl({ date_from: viewFrom, date_to: viewTo });
      const res = await axios.get(url);
      setMenuData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function shiftViewWeek(dir) {
    setViewFrom(toISODate(addDays(new Date(viewFrom), dir * 7)));
    setViewTo(toISODate(addDays(new Date(viewTo),   dir * 7)));
  }

  // Group board data by day key
  const grouped = menuData.reduce((acc, item) => {
    const key = item.serving_date ? toISODate(new Date(item.serving_date)) : "stale";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const viewDays   = getWeekDays(new Date(viewFrom));
  const staleItems  = grouped["stale"] || [];
  const staleByType = groupByType(staleItems);
  const [staleOpen, setStaleOpen] = useState(true);

  // ── FIX 2: Unavailable toggle (optimistic update) ─────────────────────────

  async function toggleUnavailable(id, unavailable, unavailable_until) {
    setMenuData((prev) =>
      prev.map((item) => item.id === id ? { ...item, unavailable, unavailable_until } : item)
    );
    try {
      await axios.patch(
        patchDailyMenuUrl(id),
        { unavailable, unavailable_until }
      );
    } catch (err) {
      console.error(err);
      loadMenuData(); // revert on error
    }
  }

  // ── Modal helpers ────────────────────────────────────────────────────────────

  async function openModal() {
    setModalOpen(true);
    setSaveMsg("");
    setSaveMsgIsError(false);

    // Default modal week to the currently viewed week in the board
    const weekToLoad = viewFrom;
    setModalWeekStart(weekToLoad);

    // Load catalog items if not yet loaded
    if (catalogItems.length === 0) {
      try {
        const res = await axios.get(GET_MENU_ITEMS_URL);
        setCatalogItems(res.data);
      } catch (err) {
        console.error(err);
      }
    }

    await loadModalSchedule(weekToLoad);
  }

  async function loadModalSchedule(weekStartStr, { silent = false } = {}) {
    // FIX: silent refresh (used right after saving) must not swap the modal
    // body for a loading spinner — that collapses its scroll height and
    // resets the user's scroll position back to the top.
    if (!silent) {
      setModalLoading(true);
      setSaveMsg("");
      setSaveMsgIsError(false);
      setPriceErrorKeys(new Set());
    }
    try {
      const monday = new Date(weekStartStr);
      const sunday = addDays(monday, 6);
      const url = getDailyMenuUrl({ date_from: toISODate(monday), date_to: toISODate(sunday) });
      const res = await axios.get(url);

      // Build schedule with all 7 days pre-initialised
      const schedule = {};
      getWeekDays(monday).forEach((d) => {
        schedule[toISODate(d)] = [];
      });
      schedule["stale"] = [];

      for (const item of res.data) {
        const key = item.serving_date ? toISODate(new Date(item.serving_date)) : "stale";
        if (!schedule[key]) schedule[key] = [];
        schedule[key].push({
          key: `existing-${item.id}`,
          id: item.id,
          menu_item_id: item.menu_item_id,
          name: item.name,
          item_type: item.item_type,
          menu_code: (item.menu_code || "").trim(),
          price: item.price ?? "",  // keep raw string from DB
          valid_until: item.valid_until || null,
          isNew: false,
          isDeleted: false,
        });
      }

      setModalSchedule(schedule);
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setModalLoading(false);
    }
  }

  function handleModalWeekChange(dateValue) {
    const normalized = normalizeToISODate(dateValue);
    if (!normalized) return;

    const monday = getMondayOfWeek(new Date(normalized));
    const mondayStr = toISODate(monday);
    setModalWeekStart(mondayStr);
    loadModalSchedule(mondayStr);
  }

  // ── Schedule mutations ────────────────────────────────────────────────────────

  async function lookupMenuPrice(dayKey, entryKey, menuCode) {
    const normalizedCode = String(menuCode || "").trim().replace(/\s+/g, "").toUpperCase();
    if (!normalizedCode) return;

    const requestKey = `${dayKey}:${entryKey}`;
    const requestId = (priceLookupRequests.current.get(requestKey) || 0) + 1;
    priceLookupRequests.current.set(requestKey, requestId);

    try {
      const response = await axios.get(
        GET_STANDARD_MENU_PRICE_URL(normalizedCode, dayKey === "stale" ? "" : dayKey)
      );
      if (priceLookupRequests.current.get(requestKey) !== requestId) return;

      setModalSchedule((prev) => ({
        ...prev,
        [dayKey]: (prev[dayKey] || []).map((entry) => {
          if (entry.key !== entryKey || entry.menu_code !== menuCode) return entry;
          return response.data.price === null
            ? entry
            : { ...entry, price: response.data.price, isModified: true };
        }),
      }));
    } catch (error) {
      // Lookup is optional; menu editing must continue when the request fails.
    }
  }

  function addEntry(dayKey, catalogItem) {
    const entryKey = `new-${Date.now()}-${catalogItem.id}`;
    setModalSchedule((prev) => {
      const staleEntries = (prev["stale"] || []).filter((e) => !e.isDeleted);
      const dayEntries   = (prev[dayKey]   || []).filter((e) => !e.isDeleted);
      // Pre bežný deň: aktuálny deň + stále menu; pre stálé menu: len stále menu
      const pool = dayKey === "stale" ? staleEntries : [...dayEntries, ...staleEntries];
      const code = nextCode(pool, catalogItem.item_type);
      const addedEntry = {
        key: entryKey,
        id: null,
        menu_item_id: catalogItem.id,
        name: catalogItem.name,
        item_type: catalogItem.item_type,
        menu_code: code,
        price: dayKey !== "stale" ? getDefaultPrice(dayKey, prev) : "",
        valid_until: null,
        isNew: true,
        isDeleted: false,
      };
      return {
        ...prev,
        [dayKey]: [
          ...(prev[dayKey] || []),
          addedEntry,
        ],
      };
    });
    const currentEntries = modalSchedule[dayKey] || [];
    const staleEntries = (modalSchedule["stale"] || []).filter((e) => !e.isDeleted);
    const dayEntries = currentEntries.filter((e) => !e.isDeleted);
    const pool = dayKey === "stale" ? staleEntries : [...dayEntries, ...staleEntries];
    lookupMenuPrice(dayKey, entryKey, nextCode(pool, catalogItem.item_type));
  }

  function deleteEntry(dayKey, entryKey) {
    setModalSchedule((prev) => ({
      ...prev,
      [dayKey]: prev[dayKey]
        .map((e) => {
          if (e.key !== entryKey) return e;
          if (e.isNew) return null; // new items: just remove
          return { ...e, isDeleted: true };
        })
        .filter(Boolean),
    }));
  }

  function updateEntryCode(dayKey, entryKey, code) {
    const normalizedCode = String(code || "").trim().replace(/\s+/g, "").toUpperCase();
    setModalSchedule((prev) => ({
      ...prev,
      [dayKey]: prev[dayKey].map((e) =>
        e.key === entryKey ? { ...e, menu_code: normalizedCode, isModified: true } : e
      ),
    }));
    lookupMenuPrice(dayKey, entryKey, normalizedCode);
  }

  // FIX 3: store raw string so comma decimal separators display correctly
  function updateEntryPrice(dayKey, entryKey, price) {
    setModalSchedule((prev) => ({
      ...prev,
      [dayKey]: prev[dayKey].map((e) =>
        e.key === entryKey ? { ...e, price, isModified: true } : e
      ),
    }));
    // Chybu ceny zhasni, len keď je nová hodnota už platná
    if (isValidPrice(price)) {
      setPriceErrorKeys((prev) => {
        const key = priceErrorKey(dayKey, entryKey);
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  function updateEntryValidUntil(dayKey, entryKey, valid_until) {
    setModalSchedule((prev) => ({
      ...prev,
      [dayKey]: prev[dayKey].map((e) =>
        e.key === entryKey ? { ...e, valid_until, isModified: true } : e
      ),
    }));
  }

  // ── Create new meal callback ──────────────────────────────────────────────────

  function handleMealCreated(newMeal) {
    setIsCreateMealOpen(false);
    if (pendingDayForNewMeal && newMeal?.id) {
      setCatalogItems((prev) => [...prev, newMeal]);
      addEntry(pendingDayForNewMeal, newMeal);
    }
    setPendingDayForNewMeal(null);
    setPendingMealCategory(null);
  }

  // ── Save ──────────────────────────────────────────────────────────────────────

  async function saveModal() {
    // Menu s cenou 0 (alebo prázdnou cenou) sa nesmie uložiť – zastav ešte pred zápisom
    // a označ konkrétne riadky, aby ich používateľ našiel.
    const invalidKeys = new Set();
    for (const [dayKey, entries] of Object.entries(modalSchedule)) {
      for (const entry of entries) {
        if (entry.isDeleted) continue;
        const willBeSaved = entry.isNew || (entry.isModified && entry.id);
        if (!willBeSaved) continue;
        if (!isValidPrice(entry.price)) invalidKeys.add(priceErrorKey(dayKey, entry.key));
      }
    }
    setPriceErrorKeys(invalidKeys);
    if (invalidKeys.size > 0) {
      setSaveMsgIsError(true);
      setSaveMsg(
        invalidKeys.size === 1
          ? "Cena musí byť kladné číslo. Opravte označenú položku."
          : `Cena musí byť kladné číslo. Opravte označené položky (${invalidKeys.size}).`
      );
      return;
    }

    setSavingModal(true);
    setSaveMsg("");
    setSaveMsgIsError(false);
    try {
      const toDelete = [];
      const toInsert = [];
      const toUpdate = []; // existing entries with changed price/code

      for (const [dayKey, entries] of Object.entries(modalSchedule)) {
        for (const entry of entries) {
          if (entry.isDeleted && entry.id) {
            toDelete.push(entry.id);
          } else if (entry.isNew && !entry.isDeleted) {
            toInsert.push({
              menu_item_id: entry.menu_item_id,
              menu_code:    entry.menu_code || null,
              serving_date: dayKey === "stale" ? null : dayKey,
              // FIX 3: properly parse price, handles both dot and comma decimal
              price:        parsePrice(entry.price),
              valid_until:  dayKey === "stale" ? (entry.valid_until || null) : null,
            });
          } else if (!entry.isNew && !entry.isDeleted && entry.isModified && entry.id) {
            toUpdate.push({
              id:         entry.id,
              price:      parsePrice(entry.price),
              menu_code:  entry.menu_code || null,
              valid_until: dayKey === "stale" ? (entry.valid_until || null) : undefined,
            });
          }
        }
      }

      // Delete marked items
      for (const id of toDelete) {
        await axios.delete(deleteDailyMenuUrl(id));
      }

      // Update modified existing items
      for (const u of toUpdate) {
        const patchBody = { price: u.price, menu_code: u.menu_code };
        if (typeof u.valid_until !== "undefined") patchBody.valid_until = u.valid_until;
        await axios.patch(
          patchDailyMenuUrl(u.id),
          patchBody
        );
      }

      // Batch insert new items
      if (toInsert.length > 0) {
        await axios.put(
          saveDailyMenuUrl,
          { items: toInsert }
        );
      }

      setSaveMsgIsError(false);
      setSaveMsg(`Zmeny boli uložené.`);
      await loadMenuData();
      await loadModalSchedule(modalWeekStart, { silent: true });
    } catch (err) {
      console.error(err);
      setSaveMsgIsError(true);
      setSaveMsg(err?.response?.data?.message || "Chyba pri ukladaní. Skúste znova.");
    } finally {
      setSavingModal(false);
    }
  }

  // ── Modal week days ───────────────────────────────────────────────────────────
  const modalWeekDays = getWeekDays(new Date(modalWeekStart));
  const modalWeekEnd  = addDays(new Date(modalWeekStart), 6);
  // FIX 4: flat list of ALL schedule entries for global code deduplication
  const allScheduleEntries = Object.values(modalSchedule).flat();

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="dm">
      {/* ── Page header ── */}
      <div className="dm__header">
        <h1 className="dm__title">Správa obedového menu</h1>
        <Button variant="primary" onClick={openModal}>
          <CalendarRange size={16} />
          Zostaviť týždenné menu
        </Button>
      </div>

      {/* ── Week navigator + date filter ── */}
      <div className="dm__filter">
        <button className="dm__week-nav" onClick={() => shiftViewWeek(-1)} title="Predchádzajúci týždeň">
          <ChevronLeft size={18} />
        </button>
        <div className="dm__filter-pickers">
          <DatePicker
            label="Od"
            value={viewFrom}
            onChange={(nextDate) => setViewFrom((current) => normalizeToISODate(nextDate, current))}
          />
          <span className="dm__filter-sep">–</span>
          <DatePicker
            label="Do"
            value={viewTo}
            onChange={(nextDate) => setViewTo((current) => normalizeToISODate(nextDate, current))}
          />
        </div>
        <button className="dm__week-nav" onClick={() => shiftViewWeek(1)} title="Nasledujúci týždeň">
          <ChevronRight size={18} />
        </button>
      </div>

      {loading ? (
        <div className="dm__loading">
          <Loader2 size={22} className="dm__spinner" />
          Načítavam…
        </div>
      ) : (
        <>
          {/* All 7 days stacked – each collapsible */}
          <div className="dm-week">
            {viewDays.map((date) => (
              <CollapsibleDayView
                key={toISODate(date)}
                date={date}
                items={grouped[toISODate(date)] || []}
                onToggleUnavailable={toggleUnavailable}
              />
            ))}
          </div>

          {/* Stale menu – always at the bottom, applies every day */}
          {staleItems.length > 0 && (
            <div className={`dm-day-card dm-day-card--stale${staleOpen ? " dm-day-card--open" : ""}`}>
              <button className="dm-day-card__header" onClick={() => setStaleOpen((v) => !v)}>
                <div className="dm-day-card__title">
                  <BookOpen size={15} className="dm-day-card__stale-icon" />
                  <span className="dm-day-card__name">Stále menu</span>
                  <span className="dm-day-card__date">— platné každý deň</span>
                </div>
                <div className="dm-day-card__right">
                  <span className="dm-day-card__count">{staleItems.length}</span>
                  <ChevronDown size={16} className={`dm-day-card__chevron${staleOpen ? " dm-day-card__chevron--open" : ""}`} />
                </div>
              </button>

              {staleOpen && (
                <div className="dm-day-card__body">
                  {staleByType.map(({ type, meta, items }) => {
                    const { Icon, label } = meta;
                    return (
                      <div key={type} className="dm-day-card__section">
                        <div className="dm-day-card__section-header"><Icon size={14} /> {label}</div>
                        <div className="dm-day-card__cards">
                          {items.map((item) => (
                            <BoardItemCard key={item.id} item={item} onToggleUnavailable={toggleUnavailable} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══ Weekly Menu Builder Modal ════════════════════════════════════════ */}
      <Modal
        isOpen={modalOpen && !isCreateMealOpen}
        onClose={() => setModalOpen(false)}
        title="Zostavenie týždenného menu"
        className="dm-modal-overlay"
        footer={
          <div className="dm-modal__footer">
            {saveMsg && (
              <span className={`dm-modal__save-msg${saveMsgIsError ? " dm-modal__save-msg--error" : ""}`}>
                {saveMsg}
              </span>
            )}
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={savingModal}>
              Zavrieť
            </Button>
            <Button variant="primary" onClick={saveModal} disabled={savingModal || modalLoading}>
              {savingModal
                ? <><Loader2 size={14} className="dm__spinner" /> Ukladám…</>
                : <><Save size={14} /> Uložiť menu</>
              }
            </Button>
          </div>
        }
      >
        {/* Week picker */}
        <div className="dm-modal__week-row">
          <DatePicker
            label="Vyberte týždeň (ľubovoľný deň)"
            value={modalWeekStart}
            onChange={handleModalWeekChange}
          />
          <div className="dm-modal__week-label">
            {formatDateSK(new Date(modalWeekStart))} – {formatDateSK(modalWeekEnd)}
          </div>
        </div>

        {modalLoading ? (
          <div className="dm__loading dm__loading--modal">
            <Loader2 size={22} className="dm__spinner" /> Načítavam…
          </div>
        ) : (
          <div className="dm-modal__body">
            {/* 7 day sections */}
            {modalWeekDays.map((date) => {
              const key          = toISODate(date);
              const dayEntries   = modalSchedule[key]       || [];
              const staleEntries = modalSchedule["stale"]   || [];
              return (
                <DaySection
                  key={key}
                  dayKey={key}
                  dayLabel={`${getDayNameSK(date)}  •  ${formatDateSK(date)}`}
                  entries={dayEntries}
                  catalogItems={catalogItems}
                  topTags={topTags}
                  onAdd={addEntry}
                  onDelete={deleteEntry}
                  onUpdateCode={updateEntryCode}
                  onUpdatePrice={updateEntryPrice}
                  priceErrorKeys={priceErrorKeys}
                  allEntries={[...dayEntries, ...staleEntries]}
                  onCreateNew={(dayKey, category) => { setPendingDayForNewMeal(dayKey); setPendingMealCategory(category); setIsCreateMealOpen(true); }}
                />
              );
            })}

            {/* Stale section – uses ALL entries globally for code deduplication */}
            <DaySection
              dayKey="stale"
              dayLabel="Stále menu — platné každý deň"
              entries={modalSchedule["stale"] || []}
              catalogItems={catalogItems}
              topTags={topTags}
              onAdd={addEntry}
              onDelete={deleteEntry}
              onUpdateCode={updateEntryCode}
              onUpdatePrice={updateEntryPrice}
              onUpdateValidUntil={updateEntryValidUntil}
              priceErrorKeys={priceErrorKeys}
              allEntries={allScheduleEntries}
              isStale
              modalSunday={toISODate(modalWeekEnd)}
              onCreateNew={(dayKey, category) => { setPendingDayForNewMeal(dayKey); setPendingMealCategory(category); setIsCreateMealOpen(true); }}
            />
          </div>
        )}
      </Modal>
      {/* ═══ Create New Meal Modal ════════════════════════════════════════════ */}
      <CreateMealModal
        isOpen={isCreateMealOpen}
        onClose={() => { setIsCreateMealOpen(false); setPendingDayForNewMeal(null); setPendingMealCategory(null); }}
        onMealCreated={handleMealCreated}
        topTags={topTags}
        initialCategory={pendingMealCategory}
      />
    </div>
  );
}

export default DailyMenuSection;
